import React from "react";
//#region types
import { WintSuperkollAPIModel, WintSuperkollAPIModelAnnualAccounts } from "wint-api-client/dist/types";
import { StyleEnums } from "models/enums";
import { FinancialStetementViewParams } from "models/propTypes/urlParamsTypes";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { TranslationKeys } from "translations";
import { User } from "models/propTypes/userProfilePropTypes";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import {
  FileViewerContainer,
  PageLayouts,
  EntityTitle,
  useMountedState,
  useNavigationHistory,
  RouteBreadCrumb,
  LoadingWithLogo,
  CommentsView,
} from "components/common";
import { ErrorView } from "components/error";
import { useParams } from "react-router-dom";
import FinancialStatementViewFooter from "./financialStatementViewFooter";
import { useAnnualAccountService } from "serviceHooks";
import useAnnualAccountPermissions from "./usePermissions";
//#endregion
import { DateHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

const FinancialStatementView: React.FC = () => {
  const annualAccountService = useAnnualAccountService();
  const serviceRef = React.useRef(annualAccountService);
  serviceRef.current = annualAccountService;

  const { getMounted } = useMountedState();
  const fileCache = React.useRef<Map<string, string>>(new Map());
  const urlParams = useParams<FinancialStetementViewParams>();
  const { goBack } = useNavigationHistory();
  const [loading, setLoading] = React.useState(true);
  const [annualReport, setAnnualReport] = React.useState<WintSuperkollAPIModelAnnualAccounts.AnnualReport>();
  const [key, setKey] = React.useState(0);
  const [attachments, setAttachments] = React.useState<ImageMeta[]>([]);
  const displayType = StyleEnums.DisplayType.Info;

  const { getHasAnnualAccountDocumentPermission } = useAnnualAccountPermissions();

  const reportType = !UtilsHelper.isEmpty(urlParams.reportType)
    ? (Number.parseInt(urlParams.reportType!) as WintSuperkollAPIModel.FinancialStatementType)
    : WintSuperkollAPIModel.FinancialStatementType.AnnualReportAndIncomeTaxReturn;

  const loadFinancialStatement = (id: number) => {
    annualAccountService
      .getFinancialStatement(id)
      .then((result) => {
        if (getMounted()) {
          setAnnualReport(result.data);
          setKey(key + 1);
        }
      })
      .catch(() => {
        if (annualReport)
          setTimeout(() => {
            goBack();
          }, 1000);
      })
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    const financialYearId = urlParams.id;
    if (financialYearId) {
      if (annualReport?.financialYearId !== Number.parseInt(financialYearId)) {
        setLoading(true);
        loadFinancialStatement(Number.parseInt(financialYearId));
      }
    } else {
      setAnnualReport(undefined);
      setLoading(false);
    }
  }, [urlParams]);

  React.useEffect(() => {
    if (!annualReport) return;

    annualAccountService
      .getDocumentsMeta(annualReport.financialYearId, reportType)
      .then((data) => {
        if (getMounted() && data.numberOfAttachments > 0)
          setAttachments(
            data.attachmentsMeta
              ? data.attachmentsMeta.map((meta) => ({
                  id: meta.attachmentId ?? "",
                  contentType: meta.contentType,
                  numberOfPages: meta.numberOfPages,
                  imageType: meta.imageType,
                }))
              : []
          );
      })
      .catch(() => {
        if (getMounted()) setAttachments([]);
      });
  }, [annualReport?.financialYearId, reportType]);

  const upperLeftComponent = React.useMemo(
    () =>
      annualReport ? (
        <EntityTitle
          subTitle={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.AnnualAccounts)}
          title={""}
        />
      ) : (
        <></>
      ),
    [annualReport]
  );

  const getFile = React.useCallback(
    (_attachmentId: number | string) => {
      const cacheKey = String(_attachmentId);
      const cached = fileCache.current.get(cacheKey);
      if (cached) return Promise.resolve(cached);

      const attachment = attachments.find((a) => String(a.id) === cacheKey);
      if (!annualReport || !attachment) return Promise.resolve("");
      return serviceRef.current.getDocumentFile(annualReport.financialYearId, attachment.imageType).then((data) => {
        if (data) fileCache.current.set(cacheKey, data);
        return data;
      });
    },
    [attachments, annualReport]
  );

  const lowerLeftComponent = React.useMemo(
    () => (
      <FileViewerContainer
        key={key}
        meta={attachments}
        parentId={annualReport?.financialYearId ?? 0}
        getFile={getFile}
      />
    ),
    [annualReport?.financialYearId, attachments, getFile]
  );

  return (
    <>
      <PageLayouts.EntityFullScreen
        entityKey={`financial-statement`}
        id={annualReport?.financialYearId ?? 0}
        navigation={
          <RouteBreadCrumb
            current={
              loading
                ? ""
                : `${DateHelper.formatDateRange(
                    annualReport?.financialYear?.start ?? undefined,
                    annualReport?.financialYear?.end ?? undefined,
                    "-"
                  )}`
            }
            showHome
          />
        }
        hasPermissions={getHasAnnualAccountDocumentPermission()}
      >
        {loading ? (
          <LoadingWithLogo />
        ) : annualReport ? (
          <PageLayouts.EntityViewLayout
            displayType={displayType}
            upperLeftComponent={upperLeftComponent}
            upperRightComponent={<></>}
            lowerLeftComponent={lowerLeftComponent}
            footer={<FinancialStatementViewFooter annualAccountReport={annualReport} reportType={reportType} />}
          >
            <>
              {annualReport.comments && annualReport.comments?.length !== 0 && (
                <CommentsView
                  title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Messages)}
                  icon={IconDefinitionConstants.WCommentDots}
                  parentId={annualReport.id ?? ""}
                  comments={annualReport.comments.map((message, index) => ({
                    id: index,
                    message: message.comment ?? "",
                    date: message.created,
                    from: new User(message.profileId, message.profileName),
                  }))}
                />
              )}
            </>
          </PageLayouts.EntityViewLayout>
        ) : (
          <ErrorView />
        )}
      </PageLayouts.EntityFullScreen>
    </>
  );
};

export default FinancialStatementView;
